import React from 'react';
import SkeletonBlock from '../ui/SkeletonBlock';
import SkeletonRoot from '../ui/SkeletonRoot';
import {
  adminListTableShellClass,
} from '../../utils/admin/adminChrome';

/**
 * Branded loading skeleton for list/table pages (Applications, etc.)
 * Preserves table/list geometry so route and tab switches do not collapse.
 */
interface ListSkeletonProps {
  rows?: number;
  showSearch?: boolean;
  showFilters?: boolean;
  title?: string;
}

const ListSkeleton: React.FC<ListSkeletonProps> = ({
  rows = 8,
  showSearch = true,
  showFilters = true,
  title = 'Loading records...',
}) => {
  const rowItems = Array.from({ length: rows }, (_, index) => index);
  const shouldRenderPageShell = showSearch || showFilters;

  const tableSkeleton = (
    <div className={adminListTableShellClass}>
      <div className="overflow-x-auto">
        <table className="min-w-full table-fixed divide-y divide-gray-200">
          <thead className="bg-slate-50">
            <tr>
              {Array.from({ length: 6 }, (_, index) => (
                <th key={index} className="px-4 py-3">
                  <SkeletonBlock className="h-3 w-3/4" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rowItems.map((row) => (
              <tr key={row}>
                {Array.from({ length: 6 }, (_, column) => (
                  <td key={column} className="px-4 py-3">
                    <SkeletonBlock
                      className={`h-4 ${
                        column === 0 ? 'w-11/12' : column === 5 ? 'w-1/2' : 'w-3/4'
                      }`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (!shouldRenderPageShell) {
    return (
      <SkeletonRoot loadingLabel={title} className="w-full">
        {tableSkeleton}
      </SkeletonRoot>
    );
  }

  return (
    <SkeletonRoot loadingLabel={title}>
      <div className="min-h-screen bg-white p-4 sm:p-6">
        <div className="mx-auto max-w-[1400px] space-y-4">
          <SkeletonBlock className="h-8 w-48 rounded-lg" />
          <div className="rounded-2xl border border-primary-500/10 bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {showSearch && (
                <SkeletonBlock className="h-10 rounded-lg md:col-span-2" />
              )}
              {showFilters && <SkeletonBlock className="h-10 rounded-lg" />}
            </div>
          </div>
          {tableSkeleton}
        </div>
      </div>
    </SkeletonRoot>
  );
};

export default ListSkeleton;
